import { makePersisted } from '@solid-primitives/storage';
import {
  ConfigFeatureFlagProvider,
  FeatureFlagsProvider,
  getFlags,
  getSourceFlagName,
  type ConfigFeatureFlagValues,
} from '@trimble/feature-flags';
import { createContext, createSignal, JSX, useContext } from 'solid-js';
import { PERSISTENCE_KEY } from './constants/make-persisted-constants';
import {
  MOBILE_MAPPING_FEATURE_FLAG_DEFINITIONS,
  type MobileMappingFlag,
} from './feature-flags/mobile-mapping-feature-flags';

type Overrides = Readonly<Record<string, boolean>>;

const CONFIG_FLAGS: readonly MobileMappingFlag[] = getFlags(
  MOBILE_MAPPING_FEATURE_FLAG_DEFINITIONS
).filter(
  (flag) =>
    getSourceFlagName(
      MOBILE_MAPPING_FEATURE_FLAG_DEFINITIONS,
      flag,
      'config'
    ) !== undefined
);

const getConfigName = (flag: MobileMappingFlag): string | undefined =>
  getSourceFlagName(MOBILE_MAPPING_FEATURE_FLAG_DEFINITIONS, flag, 'config');

export interface FeatureFlagOverridesContextType {
  /** The flags that have a config name, in definition order. */
  readonly flags: readonly MobileMappingFlag[];
  setOverride(flag: MobileMappingFlag, enabled: boolean): void;
}

const FeatureFlagOverridesContext =
  createContext<FeatureFlagOverridesContextType>();

export interface FeatureFlagOverridesProviderProps {
  children: JSX.Element;
  /** Per-environment defaults, keyed by config name. */
  defaults?: ConfigFeatureFlagValues;
}

/**
 * Provides the feature flags with the config defaults, overridden by values
 * the user set in Debug Settings and kept in localStorage.
 */
export function FeatureFlagOverridesProvider(
  props: Readonly<FeatureFlagOverridesProviderProps>
): JSX.Element {
  const isDefaultEnabled = (name: string): boolean =>
    props.defaults?.[name] ?? false;

  const [overrides, setOverrides] = makePersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<Overrides>({}),
    { name: PERSISTENCE_KEY.FeatureFlagOverrides }
  );

  // Drop overrides of unknown flags and overrides equal to their default, so a
  // stale entry cannot pin a user to an old value after the default changes.
  const pruned: Record<string, boolean> = {};
  const stored: unknown = overrides();
  if (typeof stored === 'object' && stored !== null) {
    for (const flag of CONFIG_FLAGS) {
      const name = getConfigName(flag) ?? '';
      const value: unknown = Reflect.get(stored, name);
      if (typeof value === 'boolean' && value !== isDefaultEnabled(name)) {
        pruned[name] = value;
      }
    }
  }
  setOverrides(pruned);

  const setOverride = (flag: MobileMappingFlag, enabled: boolean): void => {
    const name = getConfigName(flag);
    if (name === undefined) {
      return;
    }
    const next = { ...overrides() };
    if (enabled === isDefaultEnabled(name)) {
      delete next[name];
    } else {
      next[name] = enabled;
    }
    setOverrides(next);
  };

  const configSource = new ConfigFeatureFlagProvider(
    MOBILE_MAPPING_FEATURE_FLAG_DEFINITIONS,
    () => ({ ...props.defaults, ...overrides() })
  );

  return (
    <FeatureFlagOverridesContext.Provider
      value={{ flags: CONFIG_FLAGS, setOverride }}
    >
      <FeatureFlagsProvider sources={[configSource]}>
        {props.children}
      </FeatureFlagsProvider>
    </FeatureFlagOverridesContext.Provider>
  );
}

export const useFeatureFlagOverrides = (): FeatureFlagOverridesContextType => {
  const context = useContext(FeatureFlagOverridesContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagOverrides must be used within a FeatureFlagOverridesProvider'
    );
  }
  return context;
};
